/* eslint-disable no-console */
import { Ability } from '@casl/ability';
import { useTableState } from '@dispatch/shared/hooks';
import {
  Actions,
  Region,
  ResultStatus,
  Subjects,
} from '@dispatch/shared/types';
import {
  activeColumn,
  boldLinkColumn,
  CreateNewEntityButton,
  dateColumn,
  PageComponent,
  TableComponent,
  TableProps,
  textColumn,
} from '@dispatch/web/ui';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface RegionProps {
  rows?: Region[];
  status: ResultStatus;
  permissions: Ability<[Actions, Subjects]> | undefined;
}

export function Regions({ rows = [], status, permissions }: RegionProps) {
  const { tableState, setTableState } = useTableState();
  const [data, setData] = useState<Region[] | undefined>();
  const [searchText, setSearchText] = useState<string>('');
  const navigate = useNavigate();
  useEffect(() => {
    setData([...rows]);
  }, [rows]);

  const hasPermissions = !!permissions?.can('set', 'regions');

  const columns: GridColDef[] = [
    boldLinkColumn<Region, string>(
      {
        field: 'name',
        headerName: 'Region Name',
        sortable: false,
      },
      (region, _rowValue) => `/admin/regions/${region?.id}`
    ),
    textColumn({
      field: 'country',
      headerName: 'Country',
      sortable: false,
    }),
    textColumn({
      field: 'states',
      headerName: 'States/Provinces',
      valueGetter: (params) => {
        return params.row.states?.join(', ');
      },
      sortable: false,
    }),
    dateColumn({
      field: 'createdAt',
      headerName: 'Created',
      maxWidth: 170,
      sortable: false,
      valueGetter: (params) => new Date(params.row.createdAt),
    }),
    dateColumn({
      field: 'editedAt',
      headerName: 'Edited',
      maxWidth: 170,
      sortable: false,
      valueGetter: (params) => new Date(params.row.createdAt),
    }),
    activeColumn({
      field: 'isActive',
      headerName: 'Active',
      sortable: false,
    }),
  ];

  const onSearch = (value: string) => {
    setSearchText(value);
    const dataClone = [...rows];
    if (value) {
      const filterData = dataClone.filter((row: Region) =>
        row.name?.toLocaleLowerCase().includes(value.toLocaleLowerCase())
      );
      return setData(filterData);
    }

    setData(dataClone);
  };

  const tableArgs: TableProps<Region> = {
    tableState: {
      columnModel: tableState['regions'].columnModel,
      pinnedColumns: {
        ...tableState['regions'].pinnedColumns,
      },
    },
    handleShowColumns: (newModel) => {
      if (!newModel) return;
      setTableState({
        ...tableState,
        regions: { ...tableState['regions'], columnModel: newModel },
      });
    },
    handlePinnedColumns: (newPinned) => {
      setTableState({
        ...tableState,
        regions: { ...tableState['regions'], pinnedColumns: newPinned },
      });
    },
    rows: data || [],
    columns,
    status: searchText !== '' && data && data.length <= 0 ? 'noresult' : status,
    emptyEntityName: 'Region',
  };

  return (
    <PageComponent
      title="Regions"
      onSearch={onSearch}
      searchPlaceholder="Name"
      actions={
        hasPermissions && (
          <CreateNewEntityButton
            onClick={() => navigate('/admin/regions/add')}
            entityName="Region"
          />
        )
      }
    >
      <Box width="100%">
        <TableComponent {...tableArgs} />
      </Box>
    </PageComponent>
  );
}

export default Regions;
